import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  User,
  MapPin,
  Star,
  Clock,
  Award,
  MessageSquare,
  ArrowLeft,
  Mail,
  Phone,
  Calendar,
  DollarSign,
  Briefcase,
  CheckCircle,
  Bookmark,
  BookmarkCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { applyPageMetadata } from '../utils/seo';

function BartenderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isBarOwner } = useAuth();
  const [bartender, setBartender] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [savingToggle, setSavingToggle] = useState(false);

  useEffect(() => {
    fetchBartenderDetails();
  }, [id]);

  useEffect(() => {
    const loadSavedState = async () => {
      if (!user || !isBarOwner || !id) {
        setIsSaved(false);
        return;
      }
      try {
        const response = await axios.get('/owners/saved-bartender-ids');
        const ids = response.data?.saved_bartender_ids || [];
        setIsSaved(ids.includes(id));
      } catch (error) {
        console.error('Failed to load saved state:', error);
      }
    };
    loadSavedState();
  }, [user, isBarOwner, id]);

  const fetchBartenderDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/bartenders/${id}`);
      setBartender(response.data);
    } catch (error) {
      console.error('Failed to fetch bartender details:', error);
      toast.error('Failed to load bartender profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = () => {
    if (!user) {
      toast.info('Create a free owner account to message bartenders, view full profiles, and hire faster.');
      navigate('/register');
      return;
    }

    if (!isBarOwner) {
      toast.error('Only bar owners can contact bartenders.');
      return;
    }

    navigate(`/messages?user=${id}`);
  };

  const handleSaveToggle = async () => {
    if (!user || !isBarOwner) {
      toast.info('Create a free owner account to save bartenders and contact your shortlist.');
      navigate('/register');
      return;
    }

    try {
      setSavingToggle(true);
      if (isSaved) {
        await axios.delete(`/owners/saved-bartenders/${id}`);
        setIsSaved(false);
        toast.success('Removed from Saved Bartenders');
      } else {
        await axios.post(`/owners/saved-bartenders/${id}`);
        setIsSaved(true);
        toast.success('Saved to Saved Bartenders');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update saved bartender');
    } finally {
      setSavingToggle(false);
    }
  };

  const availableTonightStatus = bartender?.available_tonight ?? bartender?.profile?.available_tonight;
  const verificationStatus = bartender?.verification_status ?? bartender?.profile?.verification_status;
  const canContactBartender = Boolean(user && isBarOwner);

  useEffect(() => {
    if (!bartender) return undefined;

    const firstName = bartender.user?.first_name || 'Bartender';
    const city = bartender.location?.city || 'Local';

    const cleanup = applyPageMetadata({
      title: `${firstName} Bartender Profile | BarsConnect`,
      description: `View ${firstName}'s bartender profile in ${city}, including skills, verification status, and reviews on BarsConnect.`,
      canonicalUrl: `https://barsconnect.com/bartenders/${id}`,
      robots: 'index,follow',
    });

    return cleanup;
  }, [bartender, id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-5xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-white/5 rounded w-1/4"></div>
            <div className="h-64 bg-white/5 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!bartender) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Bartender Not Found</h2>
          <Button onClick={() => navigate('/bartenders')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Bartenders
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/bartenders')}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Bartenders
        </Button>

        {/* Header Card with Profile Info */}
        <Card className="bg-white/5 backdrop-blur-md border border-white/5">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Avatar */}
              <div className="flex-shrink-0">
                <Avatar className="h-32 w-32">
                  <AvatarImage
                    src={bartender.user?.profile_picture}
                    alt={`${bartender.user?.first_name} ${bartender.user?.last_name}`}
                  />
                  <AvatarFallback className="bg-primary/10 text-primary text-3xl">
                    {bartender.user?.first_name?.[0]}{bartender.user?.last_name?.[0]}
                  </AvatarFallback>
                </Avatar>
              </div>

              {/* Info */}
              <div className="flex-1">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h1 className="text-3xl font-bold text-white mb-2" data-testid="public-bartender-profile-name">
                      {bartender.user?.first_name || 'Bartender'}
                    </h1>
                    <div className="flex items-center gap-4 text-muted-foreground">
                      {bartender.average_rating > 0 && (
                        <div className="flex items-center">
                          <Star className="h-5 w-5 text-yellow-500 fill-yellow-500 mr-1" />
                          <span className="font-semibold text-white">
                            {bartender.average_rating?.toFixed(1)}
                          </span>
                        <span className="ml-1">({bartender.total_reviews || 0} reviews)</span>
                        </div>
                      )}
                      {bartender.experience_years && (
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-1" />
                          <span>{bartender.experience_years} years experience</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {isBarOwner && (
                      <Button
                        onClick={handleSaveToggle}
                        size="lg"
                        variant="outline"
                        disabled={savingToggle}
                        data-testid="bartender-detail-save-btn"
                      >
                        {isSaved ? <BookmarkCheck className="mr-2 h-4 w-4" /> : <Bookmark className="mr-2 h-4 w-4" />}
                        {isSaved ? 'Saved' : 'Save'}
                      </Button>
                    )}
                    <Button onClick={handleSendMessage} size="lg" data-testid="bartender-detail-header-action-btn">
                      <MessageSquare className="mr-2 h-4 w-4" />
                      {canContactBartender ? 'Send Message' : 'Unlock Contact Information'}
                    </Button>
                  </div>
                </div>

                {/* Badges */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {availableTonightStatus && (
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30" data-testid="bartender-detail-available-tonight-badge">
                      Available Tonight
                    </Badge>
                  )}
                  {verificationStatus === 'approved' && (
                    <Badge variant="default" className="bg-green-500/20 text-green-400" data-testid="bartender-detail-verified-badge">
                      <CheckCircle className="mr-1 h-3 w-3" />
                      Verified Bartender
                    </Badge>
                  )}
                  {bartender.certifications?.map((cert, index) => (
                    <Badge key={index} variant="secondary">
                      <Award className="mr-1 h-3 w-3" />
                      {cert}
                    </Badge>
                  ))}
                </div>

                {!user && (
                  <p className="text-sm text-muted-foreground" data-testid="public-bartender-profile-supporting-gate-copy">
                    Create a free owner account to message bartenders, view full profiles, and hire faster.
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {!user ? (
          <Card className="bg-white/5 backdrop-blur-md border border-white/5" data-testid="public-bartender-profile-gate-card">
            <CardContent className="p-6 text-center">
              <h3 className="text-xl font-semibold text-white mb-2">Unlock Full Bartender Profile</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create a free owner account to message bartenders, view full profiles, and hire faster.
              </p>
              <Button onClick={() => navigate('/register')} data-testid="public-bartender-profile-unlock-btn">
                Unlock Contact Information
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Experience & Skills */}
              <Card className="bg-white/5 backdrop-blur-md border border-white/5">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Briefcase className="mr-2 h-5 w-5" />
                    Experience & Skills
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {bartender.specialties && bartender.specialties.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-muted-foreground mb-2">Specialties</h4>
                      <div className="flex flex-wrap gap-2">
                        {bartender.specialties.map((specialty, index) => (
                          <Badge key={index} variant="outline">
                            {specialty}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {bartender.skills && bartender.skills.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-muted-foreground mb-2">Skills</h4>
                      <div className="flex flex-wrap gap-2">
                        {bartender.skills.map((skill, index) => (
                          <Badge key={index} variant="secondary">
                            {skill}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Availability & Rate */}
              <Card className="bg-white/5 backdrop-blur-md border border-white/5">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Calendar className="mr-2 h-5 w-5" />
                    Availability & Rate
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {bartender.availability && (
                    <div>
                      <h4 className="text-sm font-semibold text-muted-foreground mb-2">Available</h4>
                      <div className="flex flex-wrap gap-2">
                        {bartender.availability.map((day, index) => (
                          <Badge key={index} variant="outline">
                            {day}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {(bartender.hourly_rate_min || bartender.hourly_rate_max) && (
                    <div className="p-4 bg-primary/5 rounded-lg border border-primary/10">
                      <div className="flex items-center mb-1">
                        <DollarSign className="h-4 w-4 mr-1 text-primary" />
                        <h4 className="text-sm font-semibold text-muted-foreground">Hourly Rate</h4>
                      </div>
                      <p className="text-2xl font-bold text-primary">
                        {bartender.hourly_rate_min && bartender.hourly_rate_max
                          ? `$${bartender.hourly_rate_min} - $${bartender.hourly_rate_max}`
                          : bartender.hourly_rate_min
                          ? `From $${bartender.hourly_rate_min}`
                          : `Up to $${bartender.hourly_rate_max}`}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Portfolio Images */}
            {isBarOwner && bartender.portfolio_images && bartender.portfolio_images.length > 0 && (
              <Card className="bg-white/5 backdrop-blur-md border border-white/5">
                <CardHeader>
                  <CardTitle>Portfolio</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {bartender.portfolio_images.map((image, index) => (
                      <img
                        key={index}
                        src={image}
                        alt={`Portfolio ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg border border-white/10"
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Contact Button */}
            <div className="flex justify-center">
              <Button onClick={handleSendMessage} size="lg" className="w-full md:w-auto" data-testid="bartender-detail-contact-action-btn">
                <MessageSquare className="mr-2 h-5 w-5" />
                {canContactBartender ? `Contact ${bartender.user?.first_name || 'Bartender'}` : 'Unlock Contact Information'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default BartenderDetailPage;
